import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  ClipboardList,
  LogOut,
  Eye,
  Download,
  CheckCircle,
  XCircle,
  FileText,
  Building2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Loader2,
  AlertCircle,
  Users,
  Clock,
} from "lucide-react";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState([]);
  const [approvedAgencies, setApprovedAgencies] = useState([]);
  const [rejectedAgencies, setRejectedAgencies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("pending");
  const [docModal, setDocModal] = useState({ open: false, url: "", filename: "", loading: false });

  const adminToken = localStorage.getItem("admin_token");
  const adminName = localStorage.getItem("admin_name");

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin/login");
      return;
    }
    fetchAgencies();
    fetchApprovedAgencies();
    fetchRejectedAgencies();
  }, [adminToken, navigate]);

  const fetchAgencies = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/agencies/pending`,
        {
          headers: { Authorization: `Bearer ${adminToken}` },
        }
      );

      if (response.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
        return;
      }

      const result = await response.json();
      setAgencies(result.agencies || []);
    } catch (error) {
      toast.error("Error al cargar las solicitudes");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchApprovedAgencies = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/agencies/approved`,
        {
          headers: { Authorization: `Bearer ${adminToken}` },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setApprovedAgencies(result.agencies || []);
      }
    } catch (error) {
      console.error("Error fetching approved agencies:", error);
    }
  };

  const fetchRejectedAgencies = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/agencies/rejected`,
        {
          headers: { Authorization: `Bearer ${adminToken}` },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setRejectedAgencies(result.agencies || []);
      }
    } catch (error) {
      console.error("Error fetching rejected agencies:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_name");
    navigate("/admin/login");
  };

  const viewDocument = async (agencyId, docType) => {
    setDocModal({ open: true, url: "", filename: docType.toUpperCase(), loading: true });
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/document/${agencyId}/${docType}?token=${adminToken}`
      );
      if (!response.ok) throw new Error("Error loading document");
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const contentDisposition = response.headers.get("content-disposition");
      const filename = contentDisposition
        ? contentDisposition.split("filename=")[1]?.replace(/"/g, "") || `${docType}.pdf`
        : `${docType}.pdf`;
      setDocModal({ open: true, url: blobUrl, filename, loading: false, contentType: blob.type });
    } catch {
      setDocModal({ open: false, url: "", filename: "", loading: false });
      toast.error("Error al cargar el documento");
    }
  };

  const closeDocModal = () => {
    if (docModal.url) URL.revokeObjectURL(docModal.url);
    setDocModal({ open: false, url: "", filename: "", loading: false });
  };

  const handleDocDownload = () => {
    if (!docModal.url) return;
    const link = document.createElement("a");
    link.href = docModal.url;
    link.download = docModal.filename;
    link.click();
  };

  const handleApprove = async (agency) => {
    setActionLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/agencies/${agency.id}/approve`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${adminToken}` },
        }
      );

      if (response.ok) {
        toast.success(`Solicitud de ${agency.name} aprobada`);
        fetchAgencies();
        fetchApprovedAgencies();
      } else {
        const result = await response.json();
        toast.error(result.detail || "Error al aprobar");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (agency) => {
    setSelectedAgency(agency);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Debe indicar un motivo de rechazo");
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/agencies/${selectedAgency.id}/reject`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${adminToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reason: rejectReason }),
        }
      );

      if (response.ok) {
        toast.success(`Solicitud de ${selectedAgency.name} rechazada`);
        setShowRejectModal(false);
        fetchAgencies();
        fetchRejectedAgencies();
      } else {
        const result = await response.json();
        toast.error(result.detail || "Error al rechazar");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-800 text-white flex flex-col">
        <div className="p-6 border-b border-slate-700">
          <img
            src="https://customer-assets.emergentagent.com/job_altas-demo/artifacts/tufzwqkc_bluteca_logo_no_bg.png"
            alt="Bluteca Logo"
            className="h-10 brightness-0 invert"
          />
          <p className="text-slate-400 text-sm mt-2">Panel Admin</p>
        </div>

        <nav className="flex-1 p-4">
          <div className="space-y-2">
            <button
              onClick={() => setActiveTab("pending")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "pending" 
                  ? "bg-blue-600 text-white" 
                  : "text-slate-300 hover:bg-slate-700"
              }`}
            >
              <ClipboardList className="w-5 h-5" />
              <span>Nuevas Registraciones</span>
              {agencies.length > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {agencies.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("approved")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "approved" 
                  ? "bg-blue-600 text-white" 
                  : "text-slate-300 hover:bg-slate-700"
              }`}
            >
              <Users className="w-5 h-5" />
              <span>Clientes Aprobados</span>
              {approvedAgencies.filter(a => a.days_since_approval >= 14).length > 0 && (
                <span className="ml-auto bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {approvedAgencies.filter(a => a.days_since_approval >= 14).length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("rejected")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "rejected" 
                  ? "bg-blue-600 text-white" 
                  : "text-slate-300 hover:bg-slate-700"
              }`}
            >
              <XCircle className="w-5 h-5" />
              <span>No Aprobados</span>
              {rejectedAgencies.length > 0 && (
                <span className="ml-auto bg-slate-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {rejectedAgencies.length}
                </span>
              )}
            </button>
          </div>
        </nav>

        <div className="p-4 border-t border-slate-700">
          <p className="text-sm text-slate-400 mb-3">Hola, {adminName || "Admin"}</p>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar sesión
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        {activeTab === "pending" ? (
          <>
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-slate-800">Nuevas Registraciones</h1>
              <p className="text-slate-500">Solicitudes pendientes de revisión</p>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : agencies.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-800">¡Todo al día!</h3>
                <p className="text-slate-500">No hay solicitudes pendientes de revisión.</p>
              </div>
            ) : (
              <div className="space-y-6">
            {agencies.map((agency) => (
              <div
                key={agency.id}
                className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
              >
                {/* Header */}
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    <div>
                      <h3 className="font-semibold text-slate-800">{agency.name}</h3>
                      <p className="text-sm text-slate-500">{agency.commercial_name || "Sin nombre comercial"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {agency.email_verified ? (
                      <span className="flex items-center gap-1 text-green-600 text-sm bg-green-50 px-3 py-1 rounded-full">
                        <CheckCircle className="w-4 h-4" />
                        Email verificado
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-600 text-sm bg-amber-50 px-3 py-1 rounded-full">
                        <AlertCircle className="w-4 h-4" />
                        Email pendiente
                      </span>
                    )}
                  </div>
                </div>

                {/* Body */}
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Datos de la empresa */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-slate-700 text-sm uppercase tracking-wide">Datos de la empresa</h4>
                      <div className="space-y-2 text-sm">
                        <p className="flex items-center gap-2 text-slate-600">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">NIF/CIF:</span> {agency.legal_id}
                        </p>
                        <p className="flex items-center gap-2 text-slate-600">
                          <Mail className="w-4 h-4 text-slate-400" />
                          {agency.email}
                        </p>
                        <p className="flex items-center gap-2 text-slate-600">
                          <Phone className="w-4 h-4 text-slate-400" />
                          {agency.phone}
                        </p>
                        <p className="flex items-center gap-2 text-slate-600">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          {agency.address_city}, {agency.address_province}
                        </p>
                        <p className="flex items-center gap-2 text-slate-600">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {formatDate(agency.created)}
                        </p>
                      </div>
                    </div>

                    {/* Documentos */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-slate-700 text-sm uppercase tracking-wide">Documentos</h4>
                      <div className="space-y-2">
                        {/* CIF */}
                        <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <span className="text-sm text-slate-700">Tarjeta CIF</span>
                          </div>
                          <button
                            onClick={() => viewDocument(agency.id, "cif")}
                            className="p-1.5 hover:bg-slate-200 rounded"
                            title="Ver documento"
                          >
                            <Eye className="w-4 h-4 text-slate-600" />
                          </button>
                        </div>

                        {/* Modelo 036/037 */}
                        <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <span className="text-sm text-slate-700">Modelo 036/037</span>
                          </div>
                          <button
                            onClick={() => viewDocument(agency.id, "modelo")}
                            className="p-1.5 hover:bg-slate-200 rounded"
                            title="Ver documento"
                          >
                            <Eye className="w-4 h-4 text-slate-600" />
                          </button>
                        </div>

                        {/* DNI */}
                        <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <span className="text-sm text-slate-700">DNI Representante</span>
                          </div>
                          <button
                            onClick={() => viewDocument(agency.id, "dni")}
                            className="p-1.5 hover:bg-slate-200 rounded"
                            title="Ver documento"
                          >
                            <Eye className="w-4 h-4 text-slate-600" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Acciones */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-slate-700 text-sm uppercase tracking-wide">Acciones</h4>
                      <div className="space-y-3">
                        <Button
                          onClick={() => handleApprove(agency)}
                          disabled={actionLoading}
                          className="w-full bg-green-600 hover:bg-green-700 text-white"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Aprobar
                        </Button>
                        <Button
                          onClick={() => openRejectModal(agency)}
                          disabled={actionLoading}
                          variant="outline"
                          className="w-full border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Rechazar
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        ) : activeTab === "rejected" ? (
          <>
            {/* Rejected Agencies Tab */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-slate-800">Clientes No Aprobados</h1>
              <p className="text-slate-500">Solicitudes que han sido rechazadas</p>
            </div>

            {rejectedAgencies.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <XCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-800">Sin clientes rechazados</h3>
                <p className="text-slate-500">No hay solicitudes rechazadas.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Empresa</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">NIF/CIF</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Email</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Rechazado</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Motivo</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Documentos</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {rejectedAgencies.map((agency) => (
                        <tr key={agency.id} className="hover:bg-slate-50">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-slate-800">{agency.name}</p>
                              {agency.commercial_name && (
                                <p className="text-sm text-slate-500">{agency.commercial_name}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600">{agency.legal_id}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{agency.email}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            {agency.rejected_at ? formatDate(agency.rejected_at).split(',')[0] : '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate" title={agency.rejected_reason}>
                            {agency.rejected_reason || '-'}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-1">
                              <button
                                onClick={() => viewDocument(agency.id, "cif")}
                                className="p-1.5 hover:bg-slate-100 rounded text-slate-500"
                                title="CIF"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => viewDocument(agency.id, "modelo")}
                                className="p-1.5 hover:bg-slate-100 rounded text-slate-500"
                                title="Modelo 036/037"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => viewDocument(agency.id, "dni")}
                                className="p-1.5 hover:bg-slate-100 rounded text-slate-500"
                                title="DNI"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Approved Agencies Tab */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-slate-800">Clientes Aprobados</h1>
              <p className="text-slate-500">Clientes con acceso a la plataforma</p>
            </div>

            {approvedAgencies.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <Users className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-800">Sin clientes aprobados</h3>
                <p className="text-slate-500">Aún no hay clientes aprobados.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Legend */}
                <div className="flex gap-4 text-sm">
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-green-500"></span>
                    Dentro del período de prueba
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                    Más de 14 días (prueba por vencer)
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    Prueba expirada
                  </span>
                </div>

                {/* Table */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Empresa</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">NIF/CIF</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Email</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Aprobado</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Días</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Fin Trial</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Documentos</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {approvedAgencies.map((agency) => {
                        const daysSince = Math.floor(agency.days_since_approval || 0);
                        const isExpiring = daysSince >= 14 && daysSince < 15;
                        const isExpired = daysSince >= 15;
                        
                        return (
                          <tr 
                            key={agency.id}
                            className={`${
                              isExpired ? 'bg-red-50' : 
                              isExpiring ? 'bg-amber-50' : ''
                            }`}
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className={`w-2 h-2 rounded-full ${
                                  isExpired ? 'bg-red-500' : 
                                  isExpiring ? 'bg-amber-500' : 'bg-green-500'
                                }`}></div>
                                <div>
                                  <p className="font-medium text-slate-800">{agency.name}</p>
                                  {agency.commercial_name && (
                                    <p className="text-sm text-slate-500">{agency.commercial_name}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-600">{agency.legal_id}</td>
                            <td className="px-6 py-4 text-sm text-slate-600">{agency.email}</td>
                            <td className="px-6 py-4 text-sm text-slate-600">
                              {agency.approved_at ? formatDate(agency.approved_at).split(',')[0] : '-'}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center gap-1 text-sm font-medium ${
                                isExpired ? 'text-red-600' : 
                                isExpiring ? 'text-amber-600' : 'text-green-600'
                              }`}>
                                <Clock className="w-4 h-4" />
                                {daysSince}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-600">
                              {agency.end_date ? new Date(agency.end_date).toLocaleDateString('es-ES') : '-'}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex gap-1">
                                <button
                                  onClick={() => viewDocument(agency.id, "cif")}
                                  className="p-1.5 hover:bg-slate-100 rounded text-slate-500"
                                  title="CIF"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => viewDocument(agency.id, "modelo")}
                                  className="p-1.5 hover:bg-slate-100 rounded text-slate-500"
                                  title="Modelo 036/037"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => viewDocument(agency.id, "dni")}
                                  className="p-1.5 hover:bg-slate-100 rounded text-slate-500"
                                  title="DNI"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-800">
              Rechazar solicitud
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-4">
              Estás a punto de rechazar la solicitud de <strong>{selectedAgency?.name}</strong>.
              Por favor, indica el motivo del rechazo:
            </p>
            <Label htmlFor="reason" className="text-sm font-medium text-slate-700">
              Motivo del rechazo
            </Label>
            <textarea
              id="reason"
              className="w-full mt-2 p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={4}
              placeholder="Ej: Documentación incompleta, datos incorrectos..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRejectModal(false)}
              disabled={actionLoading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleReject}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {actionLoading ? "Procesando..." : "Confirmar rechazo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Modal */}
      <Dialog open={docModal.open} onOpenChange={closeDocModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-800">
              {docModal.filename}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 py-2">
            {docModal.loading ? (
              <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : docModal.url ? (
              docModal.contentType?.startsWith("image/") ? (
                <img
                  src={docModal.url}
                  alt={docModal.filename}
                  className="max-w-full max-h-[65vh] mx-auto object-contain rounded"
                />
              ) : (
                <iframe
                  src={docModal.url}
                  title={docModal.filename}
                  className="w-full h-[65vh] rounded border border-slate-200"
                />
              )
            ) : null}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={closeDocModal}
            >
              Cerrar
            </Button>
            <Button
              onClick={handleDocDownload}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={!docModal.url}
            >
              <Download className="w-4 h-4 mr-2" />
              Descargar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
